/**
 * Bait Selector
 * Quick-select tiles for switching bait on the fishing screen.
 * Only visible when bait slot is active (bobber/weight tackle OR fly rod equipped).
 * Filters items by rod type: fly patterns for fly rods, conventional bait otherwise.
 */

import { EquipmentSlot } from '@/types';
import { getEquipmentBySlot } from '@/data';
import { isBaitSlotActive, isEquippedRodFlyRod } from '@/data/equipment/slotConfig';
import { useEquippedGear, useOwnedEquipment } from '@/state';
import { equipmentManager } from '@/systems';

export function BaitSelector() {
  const equippedGear = useEquippedGear();
  const ownedEquipment = useOwnedEquipment();

  // Only show when bait slot is active (bobber/weight equipped OR fly rod)
  if (!isBaitSlotActive(equippedGear)) return null;

  const isFlyRod = isEquippedRodFlyRod(equippedGear);
  const equippedBaitId = equippedGear[EquipmentSlot.BAIT];
  const allBait = getEquipmentBySlot(EquipmentSlot.BAIT);

  // Filter by rod type: fly patterns (have flyPatternId) for fly rods, conventional bait otherwise
  const filteredBait = allBait.filter((b) => {
    if (isFlyRod) {
      return !!b.flyPatternId; // Only fly patterns
    }
    return !b.flyPatternId; // Only conventional bait
  });

  // Only show bait the player owns (quantity > 0)
  const ownedBait = filteredBait.filter((b) => {
    const owned = ownedEquipment[b.id];
    return owned && owned.quantity > 0;
  });

  const emptyMessage = isFlyRod
    ? 'No fly patterns available — buy them from an outfitter.'
    : 'No bait available — scavenge worms or buy bait from a shop.';

  if (ownedBait.length === 0) {
    return (
      <div className="text-center text-gray-500 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex gap-2 flex-wrap justify-center">
      {ownedBait.map((bait) => {
        const qty = ownedEquipment[bait.id]?.quantity ?? 0;
        const isEquipped = equippedBaitId === bait.id;

        // Build stat summary
        const statParts: string[] = [];
        if (bait.stats.catchChance) statParts.push(`+${Math.round(bait.stats.catchChance * 100)}% catch`);
        if (bait.stats.fishingSpeed) statParts.push(`+${Math.round(bait.stats.fishingSpeed * 100)}% speed`);
        if (bait.stats.qualityBonus) statParts.push(`+${Math.round(bait.stats.qualityBonus * 100)}% quality`);

        // For fly patterns, show target species info instead of empty stats
        if (isFlyRod && statParts.length === 0) {
          statParts.push('Fly pattern');
        }

        const handleClick = () => {
          if (isEquipped) {
            equipmentManager.unequipItem(EquipmentSlot.BAIT);
          } else {
            equipmentManager.equipItem(bait.id);
          }
        };

        return (
          <button
            key={bait.id}
            onClick={handleClick}
            className={`flex flex-col items-center px-3 py-2 rounded-lg border text-sm transition-colors min-w-[80px] min-h-[48px] ${
              isEquipped
                ? 'bg-blue-600/30 border-blue-500 text-blue-200'
                : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700/60 hover:border-gray-500'
            }`}
          >
            <span className="text-lg">{bait.icon}</span>
            <span className="font-medium text-xs">{bait.name}</span>
            <span className="text-xs text-gray-400">x{qty}</span>
            <span className={`text-[10px] mt-0.5 ${isEquipped ? 'text-blue-300' : 'text-green-400/70'}`}>
              {statParts.join(', ')}
            </span>
          </button>
        );
      })}
    </div>
  );
}
